import { CellValueChangedEvent, ColDef } from 'ag-grid-community';
import { cloneDeep } from 'lodash';

import { DEFAULT_EMPTY_TERRITORY, TERRITORY_IS_REQUIRED } from './TerritoryBasedGrid';
import { generateAggregateTableErrorMsg, getCellValueErrorMsg } from './tableValidationUtil';

export interface CellValueChangeParams {
    event: CellValueChangedEvent;
    getData: () => any[];
    updateData: (data: any[]) => void;
    enhancedColumns: ColDef[];
    contractTermKey: string;
    contractTermErrorMessages?: Record<string, Record<string, string>>;
    handleSingleErrorMsgChange: (field: string, value: string) => void;
}

export interface AddRowParams {
    agGridApi?: any;
    getData: () => any[];
    updateData: (data: any[]) => void;
    createNewRow: () => object;
    contractTermKey: string;
    contractTermErrorMessages?: Record<string, Record<string, string>>;
    handleSingleErrorMsgChange: (field: string, value: string) => void;
}

export interface DeleteRowsParams {
    agGridApi?: any;
    getData: () => any[];
    updateData: (data: any[]) => void;
    contractTermKey: string;
    contractTermErrorMessages?: Record<string, Record<string, string>>;
    handleSingleErrorMsgChange: (field: string, value: string) => void;
}

/**
 * Handles cell value changes with validation and error management
 */
export const handleCellValueChange = (params: CellValueChangeParams): void => {
    const {
        event,
        getData,
        updateData,
        enhancedColumns,
        contractTermKey,
        contractTermErrorMessages,
        handleSingleErrorMsgChange
    } = params;

    const { column, newValue, oldValue, rowIndex } = event;
    if (rowIndex === null) return;

    const newRowData = cloneDeep(getData());
    const columnId = column.getColId();

    // Update the specific field in the row data
    newRowData[rowIndex] = {
        ...newRowData[rowIndex],
        [columnId]:
            columnId === 'territory'
                ? newValue
                : typeof newValue === 'string' && !isNaN(Number(newValue))
                  ? Number(newValue)
                  : newValue
    };

    // Get the territory for this row
    const territory = newRowData[rowIndex].territory || DEFAULT_EMPTY_TERRITORY;

    // Validate all cells in this row
    const rowErrors: string[] = [];

    enhancedColumns.forEach((col) => {
        const fieldName = col.field;
        if (!fieldName || fieldName === 'checkBoxBtn') return; // Skip non-data columns

        const cellValue = newRowData[rowIndex][fieldName];
        let cellError = '';

        // Different validation based on cell editor type
        if (col.cellEditor === 'agRichSelectCellEditor') {
            // For dropdown cells, only check if required
            if (!cellValue || cellValue === DEFAULT_EMPTY_TERRITORY) {
                cellError = fieldName === 'territory' ? TERRITORY_IS_REQUIRED : 'Value is required';
            }
        } else {
            // For regular input cells, use full validation
            cellError = getCellValueErrorMsg(cellValue);
        }

        // If there's an error, add it with column name prefix
        if (cellError) {
            const columnName = col.headerName || fieldName;
            rowErrors.push(`${columnName}: ${cellError}`);
        }
    });

    // Combine all row errors into a single message
    const combinedRowError = rowErrors.join(', ');

    // Determine territories to remove from error messages
    const territoriesToRemove: string[] = [];
    if (!combinedRowError) {
        // If no errors in this row, remove this territory from error messages
        territoriesToRemove.push(territory);
    }
    if (columnId === 'territory' && oldValue && oldValue !== territory) {
        // If territory was changed, also remove the old territory
        territoriesToRemove.push(oldValue);
    }

    // Update error messages
    const updatedErrorMessage = generateAggregateTableErrorMsg(
        territory,
        combinedRowError,
        territoriesToRemove,
        contractTermErrorMessages?.[contractTermKey]?.table || ''
    );

    handleSingleErrorMsgChange('table', updatedErrorMessage);
    updateData(newRowData);
};

/**
 * Handles adding a new row with duplicate prevention
 */
export const handleAddRow = (params: AddRowParams): void => {
    const {
        agGridApi,
        getData,
        updateData,
        createNewRow,
        contractTermKey,
        contractTermErrorMessages,
        handleSingleErrorMsgChange
    } = params;

    let newRowExists = false;
    agGridApi?.forEachNode((node: any) => {
        if (node.data.territory === DEFAULT_EMPTY_TERRITORY) {
            newRowExists = true;
        }
    });
    if (newRowExists) return;

    const newRowData = cloneDeep(getData());
    newRowData.push(createNewRow());

    handleSingleErrorMsgChange(
        'table',
        generateAggregateTableErrorMsg(
            DEFAULT_EMPTY_TERRITORY,
            TERRITORY_IS_REQUIRED,
            [],
            contractTermErrorMessages?.[contractTermKey]?.table || ''
        )
    );
    updateData(newRowData);
};

/**
 * Handles deleting selected rows with error cleanup
 */
export const handleDeleteRows = (params: DeleteRowsParams): void => {
    const { agGridApi, getData, updateData, contractTermKey, contractTermErrorMessages, handleSingleErrorMsgChange } =
        params;

    const selectedRows = agGridApi?.getSelectedRows();
    if (selectedRows == undefined) return;

    const selectedTerritories: string[] = [];
    selectedRows.map((e: any) => {
        selectedTerritories.push(e.territory);
    });

    handleSingleErrorMsgChange(
        'table',
        generateAggregateTableErrorMsg(
            '',
            '',
            selectedTerritories,
            contractTermErrorMessages?.[contractTermKey]?.table || ''
        )
    );

    const newRowData = getData().filter((e) => !selectedTerritories.includes(e.territory!));
    updateData(newRowData);
};
